import { AbyssIcon } from "@/assets/icons/AbyssIcon";
import { LocationTag } from "@/constants";
import { useInsightsPageParams, useRouter } from "@/hooks";
import { abyssColors } from "@/theme/colors";
import { updateUrl } from "@/utils";
import { Button, Typography } from "@mui/material";
import queryString from "query-string";
import { useCallback, useEffect, useMemo } from "react";

export const ToggleViewButton = () => {
  const router = useRouter();
  const { inspection } = router.query;
  const query = window.location.search;
  const parsed = queryString.parse(query, { arrayFormat: 'comma' });
  const defaultPath = `/analysis/${inspection}`;
  const { getCurrentParams } = useInsightsPageParams();
  const currentMode = useMemo(() => {
    if (parsed.viewMode) {
      return parsed.viewMode
    }
    return 'classicView';
  }, [parsed.viewMode]);

  useEffect(() => {
    if (!parsed.viewMode) {
      updateUrl('viewMode', 'classicView')
      return;

    }
    const viewMode = parsed.viewMode;
    const isEnhanced = viewMode === 'enhancedView';
    if (isEnhanced) {
      router.routes.push('kendo-insights')
    }
    const desiredPath = isEnhanced
      ? `${defaultPath}/insights-kendo`
      : `/analysis/${inspection}/insights`;

    if (router.pathname !== desiredPath) {
      router.push({
        pathname: desiredPath,
        query: { ...router.query, viewMode: String(viewMode) },
      });
    }

  }, [router.query, router.pathname, inspection, getCurrentParams, router, parsed.viewMode, defaultPath]);

  const switchTo = useCallback(
    (mode: string) => {
      const path = mode === 'enhancedView' ? `${defaultPath}/insights-kendo` : `${defaultPath}/insights`;
      router.push({ pathname: path, query: { ...(router.query || {}), viewMode: mode } });
    },
    [defaultPath, router]
  );

  const handleClick = useCallback(() => {
    if (currentMode === 'enhancedView') {
      switchTo('classicView');
    } else {
      switchTo('enhancedView');
    }
  }, [currentMode, switchTo]);

  return (
    <Button
      id={`${currentMode === 'enhancedView' ? LocationTag.InsightsEnhancedView : LocationTag.InsightsClassicView}-button`}
      sx={{
        borderRadius: '0.4rem',
        height: '2.6rem',
        backgroundColor: currentMode === 'classicView' ? '#35C2A3' : undefined,
        '&:hover': {
          backgroundColor: currentMode === 'classicView' ? `${abyssColors.primary[400]}` : undefined,
        },
      }}
      onClick={handleClick}
      startIcon={<AbyssIcon
        iconName={currentMode === 'enhancedView' ? 'classic-view' : 'enhanced-view'}
        style={{ height: '1.6rem', width: '1.6rem', marginLeft: '0.2rem', marginRight: '0.2rem' }}
      />}
    >
      <Typography sx={{ fontSize: '1.2rem', color: currentMode === 'enhancedView' ? abyssColors.secondary : 'white' }}>
        {currentMode === 'enhancedView' ? 'Classic View' : 'Enhanced View'}
      </Typography>
    </Button>
  );
};
